import { CircleAlert } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import type { RootOptions } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { HOME_PATH } from "../router.tsx";
import { reportError } from "../telemetry/client-telemetry.ts";

/**
 * The root's error hooks (client-telemetry): every render error is reported, whether React
 * hands it to RootBoundary (caught) or nothing caught it, and still written to the console as
 * React does by default.
 */
export function rootErrorHooks(
  report: (error: unknown, source: "render") => void = reportError,
): Pick<RootOptions, "onUncaughtError" | "onCaughtError"> {
  const hook = (error: unknown, _info: ErrorInfo) => {
    report(error, "render");
    console.error(error);
  };
  return { onUncaughtError: hook, onCaughtError: hook };
}

/**
 * The app's last resort (#337): a render error nothing closer caught shows a calm page with a
 * reload, instead of blanking the window. It renders outside every provider (theme, session,
 * query), so it relies only on `<html data-theme>`, set before first paint, and the i18n instance.
 */
export class RootBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? <RootError /> : this.props.children;
  }
}

function RootError() {
  const { t } = useTranslation();
  return (
    <main className="grid min-h-dvh place-items-center bg-surface px-4 py-12">
      <EmptyState
        icon={CircleAlert}
        title={t("rootError.title")}
        headingLevel={1}
        className="w-full"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => window.location.reload()}>
              {t("rootError.reload")}
            </Button>
            {/* A full page load: the in-app router is part of what failed. */}
            <Button asChild size="sm" variant="outline">
              <a href={HOME_PATH}>{t("rootError.home")}</a>
            </Button>
          </div>
        }
      >
        {t("rootError.body")}
      </EmptyState>
    </main>
  );
}
